import { useEffect } from 'react';

interface KeyboardShortcutsProps {
  adjustFontSize: (delta: number) => void;
  toggleTheme: (theme: 'light' | 'dark' | 'high-contrast') => void;
  currentTheme: 'light' | 'dark' | 'high-contrast';
}

export function useKeyboardShortcuts({ 
  adjustFontSize, 
  toggleTheme, 
  currentTheme 
}: KeyboardShortcutsProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts when Alt key is pressed
      if (!event.altKey) return;

      // Prevent default browser behavior for our shortcuts
      const shortcutKeys = ['+', '=', '-', 'c', 'C', 'd', 'D'];
      if (shortcutKeys.includes(event.key)) {
        event.preventDefault();
      }

      switch (event.key) {
        case '+':
        case '=': // Handle both + and = keys (same physical key)
          adjustFontSize(10);
          announceChange('Powiększono tekst');
          break;
        
        case '-':
          adjustFontSize(-10);
          announceChange('Zmniejszono tekst');
          break;
        
        case 'c':
        case 'C':
          const nextContrastTheme = currentTheme === 'high-contrast' ? 'light' : 'high-contrast';
          toggleTheme(nextContrastTheme);
          announceChange(nextContrastTheme === 'high-contrast' ? 'Włączono wysoki kontrast' : 'Wyłączono wysoki kontrast');
          break;
        
        case 'd':
        case 'D':
          const nextDarkTheme = currentTheme === 'dark' ? 'light' : 'dark';
          toggleTheme(nextDarkTheme);
          announceChange(nextDarkTheme === 'dark' ? 'Włączono tryb ciemny' : 'Włączono tryb jasny');
          break;
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [adjustFontSize, toggleTheme, currentTheme]);

  const announceChange = (message: string) => {
    // Create or update announcement element for screen readers
    let announcement = document.getElementById('keyboard-shortcut-announcement');
    if (!announcement) {
      announcement = document.createElement('div');
      announcement.id = 'keyboard-shortcut-announcement';
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      document.body.appendChild(announcement);
    }
    
    announcement.textContent = message;
    
    // Clear the announcement after a short delay
    setTimeout(() => {
      if (announcement) {
        announcement.textContent = '';
      }
    }, 1000);
  };
}